<template>
  <div>
    <BulkOpsHeader
      @new="newDialog = true"
      buttonText="New Import"
      buttonIcon="mdi-database-plus"
    >
      Form Import
      <v-btn
        small
        flat
        :to="{ name: 'LegacyUserFormImport' }"
      >
      View Legacy Imports (pre v0.7)
      </v-btn>
    </BulkOpsHeader>
    <BulkOpsTable
      :rows.sync="formOps"
      :headers="headers"
      :totalRows="pagination.totalRows"
      :pagination="pagination"
      @fetch="pagination=arguments[0]"
    >
      <template v-slot:default="prop">
        <td>
          <span
            v-if="prop.row.status == 'running'"
          >
            <b>Running...</b>
          </span>
          <span
            v-else
          >
            <b>Imported {{prop.row.count}} Forms</b><br />
          </span>
          <span
            v-if="prop.row.failed > 0"
            style="color: red;"
          >
            ({{prop.row.failed}} failed!)
          </span>
          <div
            v-if="JSON.parse(prop.row.import_json).tags"
          >
            <b>Add Tags: </b>
            <v-chip
              v-for="tag in JSON.parse(prop.row.import_json).tags"
              :key="tag"
              small
            >
              {{tag}}
            </v-chip>
          </div>
          <div
            v-if="JSON.parse(prop.row.import_json).active"
          >
            <b>Enabled</b>
          </div>
        </td>
        <td style="text-transform: capitalize;">
          <span
            v-if="prop.row.results_csv"
          >
            <v-btn
              flat
              color="red"
              small
              @click="getErrorFile(prop.row.id)"
            >
              Download Errors
            </v-btn>
          </span>
          <v-btn
            v-if="prop.row.count > 0"
            :to="{ name: 'UserFormList', query: { filter: JSON.stringify({'bulkop': prop.row.id}) } }"
            small
            flat
          >
            View Forms
          </v-btn>
        </td>
        <td style="text-transform: capitalize;">
          {{prop.row.status}}
        </td>
      </template>
    </BulkOpsTable>
    <BulkOpsDialog
      v-if="newDialog"
      title="New Form Import"
      :update_actions="bulkOpActions"
      :disabled="cantSubmit"
      @save="save(arguments[0])"
      @cancel="newDialog = false"
      :hideOptions="!filterFormTemplate"
    > 
      <v-alert
        :value="!filterFormTemplate"
        type="info"
        dense
        style="margin-bottom: 10px;"
      >
        <b>Form Importing now supports Form Data</b>
        <br />
        As a result, all form imports must be completed using the relevent form's import file. To begin, enter the template you wish to import.
      </v-alert>
      <v-autocomplete
        v-model="filterFormTemplate"
        :loading="searching"
        :items="formTemplates"
        :search-input.sync="formSearch"
        item-value="id"
        item-text="name"
        solo
        persistent-hint
        auto-select-first
        hide-selected
        label="Template"
        hint="Template to Add to Contacts"
      >
        <template slot="no-data">
          <div
            class="autocomplete-no-data"
          >
            <div
              v-if="formSearch"
            >
              <i>No results for '{{ formSearch }}'</i>
            </div>
            <div
              v-else
            >
              Start typing to search
            </div>
          </div>
        </template>
      </v-autocomplete>
      <div
        v-if="filterFormTemplate"
        style="margin-top: 10px;"
      >
        <div style="display:inline-block;width:100%;height:32px;color:lightgrey;font-size:18px;">
          <input
            type="file"
            ref="files"
            accept=".csv"
            v-on:change="handleFileUploads()"
            required
          /><br /><br />
          <small
            style="color: black;"
          >
            <b>Do not modify the headers in an import csv</b>
            <br />
            These headers are required to match imported data to the form fields.<br />
            Modifying these import headers can result in your form being rejected.
          </small><br />
          <v-btn
            small
            color="primary"
            :disabled="loading"
            @click="downloadImportGrid('create')"
          >
            Download Create CSV
          </v-btn>
          <v-btn
            small
            color="primary"
            :disabled="loading"
            @click="downloadImportGrid('update')"
          >
            Download Update CSV
          </v-btn>
        </div>
      </div>
    </BulkOpsDialog>
  </div>
</template>

<script>
import api from '@/api'
import moment from 'moment'
import GlobalConstants from '@/globals'
import pagination from '@/components/global/pagination'
import BulkOpsHeader from '@/components/admin/bulkops/BulkOpsHeader'
import BulkOpsTable from '@/components/admin/bulkops/BulkOpsTable'
import BulkOpsDialog from '@/components/admin/bulkops/BulkOpsDialog'
import { mapGetters } from 'vuex'

export default {
  name: 'UserFormImport',
  components: {
    BulkOpsHeader,
    BulkOpsTable,
    BulkOpsDialog
  },
  mixins: [
    pagination
  ],
  data () {
    return {
      formOps: [],
      files: [],
      loading: false,
      pagination: {},
      totalRows: null,
      pages: null,
      newDialog: false,
      interval: null,
      filterFormTemplate: null,
      searching: null,
      formTemplates: [],
      formSearch: '',
      headers: [
        { text: 'Details', width: 180, sortable: false },
        { text: 'Actions', width: 60, sortable: false },
        { text: 'Status', width: 60, sortable: false },
        { text: 'Run Time', width: 60, sortable: false },
        { text: 'Progress', width: 60, sortable: false },
      ],
    }
  },
  computed: {
    cantSubmit () {
      return this.files.length == 0
    },
    bulkOpActions () {
      return [
        {
          "title": "Operation Type",
          "prop": "create_new",
          "type": "dropdown",
          "options": [
            {
              "name": "Create New Forms",
              "value": true
            },
            {
              "name": "Update Existing Forms",
              "value": false
            },
            {
              "name": "Select Operation Type",
              "value": null,
              "apionly": true
            },
          ],
          "value": null,
          "hint": "Set whether you are creating new forms or updating existing ones",
          "required": true
        },
        {
          "title": "Add Additional Tags",
          "prop": "tags",
          "type": "tags",
          "type_scope": "userform",
          "value": null,
          "hint": "Apply Tags in addition to Tags in import file"
        },
        {
          "title": "Set Status",
          "prop": "status",
          "type": "dropdown",
          "options": null,
          "value": null,
          "hint": "Set all created/updated forms to this status",
          "cond_req_prop": "create_new",
          "cond_req_value": true
        },
        {
          "type": "header",
          "title": "Advanced Options",
          "subtitle": "These options risk data loss if used improperly",
          "advanced": true
        },
        {
          "title": "Clear Form",
          "prop": "reset",
          "type": "checkbox",
          "value": false,
          "hint": "Clear Form, then import new data",
          "destructive": true,
          "advanced": true
        },
        {
          "title": "Skip Validation",
          "prop": "skip_validation",
          "type": "checkbox",
          "value": false,
          "hint": "Skip answer validation, immediately begin importing data",
          "destructive": false,
          "advanced": true
        },
      ]
    }
  },
  computed: {
    cantSubmit () {
      return this.files.length == 0
    },
    ...mapGetters('auth', [
        'getDomainInfo',
      ])
  },
  created () {
    this.documentTitle('Form Import')
  },
  watch: {
    formSearch () {
      if (this.formSearch) {
        this.loadFormTemplates()
      }
    },
    newDialog () {
      if (!this.newDialog) {
        this.filterFormTemplate = null
      }
    },
    getDomainInfo: {
      handler(){
        this.bulkOpActions[2].options = this.checkForOutage()
      },
      immediate: true
    }
  },
  methods: {
    handleFileUploads () {
      this.files = this.$refs.files.files
      if (this.files.length > 0) {
        this.canRun = true
      }
    },
    downloadImportGrid (type = "create") {
      this.loading = true
      api.formtemplate.formtemplate.downloadImportFile({'id': this.filterFormTemplate}, type)
        .then(response => {
          api.formtemplate.formtemplate.get(this.filterFormTemplate)
            .then(template => {
              this.loading = false
              let blob = new Blob([response.data])
              let link = document.createElement('a')
              link.href = window.URL.createObjectURL(blob)
              link.download = `${template.name}_import.csv`
              link.click()
            })
        })
    },
    restartInterval () {
      clearInterval(this.interval)
      var countWorking = 0
      this.formOps.forEach((item) => {
        if (["finished", "failed"].indexOf(item.status) == -1)
          countWorking++
      })
      if (countWorking > 0) {
        this.interval = setInterval(() => {
          this.fetch()
        }, 5000)
      }
    },
    fetch () {
      this.loading = true
      api.bulkops.form_import.list({
        pageNum: this.pagination.page, count: this.pagination.rowsPerPage, ordering: '-created_at'
      })
        .then(data => {
          this.formOps = data.results
          this.pagination.totalRows = data.count
          this.pagination.page = data.page_number
          this.loading = false
          this.componentCreated = true
          this.restartInterval()
        })
    },
    save (newOp) {
      let formData = new FormData()
      for( var i = 0; i < this.files.length; i++ ){
        let file = this.files[i]
        formData.append(`import_csv`, file)
      }
      var import_json = {}
      import_json['template_id'] = this.filterFormTemplate
      newOp.props.forEach((prop) => {
        if (prop.value !== null && prop.value !== "") {
          import_json[prop.prop] = prop.value
        }
      })
      formData.append(`import_json`, JSON.stringify(import_json))
      api.bulkops.form_import.save(formData)
      .then(() => {
        this.newDialog = false
        this.fetch()
      })
    },
    getErrorFile (obj) {
      this.loading = true
      api.bulkops.form_import.downloadFile(obj)
      .then(response => {
        this.loading = false
        let blob = new Blob([response.data])
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = "UserForm_Import-" + obj + '.csv'
        link.click()
      })
    },
    loadFormTemplates () {
      this.searching = true
      api.formtemplate.formtemplate.list({ search: this.formSearch, count: 15 })
        .then(data => {
          this.searching = false
          this.formTemplates = data.results
        })
    },
    checkForOutage() {
      if('outage' in this.getDomainInfo && this.getDomainInfo.outage.includes("sign")){
        return this.getStatuses().filter( status => {
          return status.name != "Request E-Signature"
        }) 
      } else {
        return this.getStatuses()
      }
    }
  },
  beforeDestroy () {
    clearInterval(this.interval)
  }
}
</script>
